import React, { Component } from 'react';

import MultiLanguageCodeBlock from 'common/developers/docs/widget/MultiLanguageCodeBlock';

import APIDocsBlock from '../APIDocsBlock';
import APIDocsTable from '../APIDocsTable';

const Snippets = {
  csharp: `using System;
using System.Net.Http;
using System.Linq;
using System.Security.Cryptography;

public class CannyWebhookValidator
{
  private static string APIKey = "YOUR_API_KEY";

  public static Boolean verifyToken(HttpResponseMessage response)
  {
    string nonce = response.Headers.GetValues("canny-nonce").First();
    string signature = response.Headers.GetValues("canny-signature").First();

    var encoding = new System.Text.UTF8Encoding();
    var hmac = new HMACSHA256(encoding.GetBytes(APIKey));
    string calculated = Convert.ToBase64String(hmac.ComputeHash(encoding.GetBytes(nonce)));

    return signature == calculated;
  }
}`,
  go: `import (
  "crypto/hmac"
  "crypto/sha256"
  "encoding/base64"
  "fmt"
  "net/http"
)

func verify(request http.Response) bool {
  nonce := request.Header["canny-nonce"][0]
  signature := request.Header["canny-signature"][0]

  APIKey := "YOUR_API_KEY"

  hash := hmac.New(sha256.New, []byte(APIKey))
  hash.Write([]byte(nonce))
  calculated := base64.StdEncoding.EncodeToString(hash.Sum(nil))

  return calculated == signature
}`,
  java: `import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import java.util.Base64;
import org.apache.http.HttpResponse;

public class CannyWebhookValidator {
  private static final String Algorithm = "HmacSHA256";
  private static final String APIKey = "YOUR_API_KEY";

  public static Boolean verifyToken(HttpResponse request) throws Exception {
    String nonce = request.headers().firstValue("canny-nonce").map(Object::toString).orElse("");
    String signature = request.headers().firstValue("canny-signature").map(Object::toString).orElse("");

    Mac hash = Mac.getInstance(Algorithm);
    hash.init(new SecretKeySpec(APIKey.getBytes(), Algorithm));

    String calculated = Base64.getEncoder().encodeToString(hash.doFinal(nonce.getBytes()));

    return calculated.equals(signature);
  }
}`,
  php: `const APIKey = "YOUR_API_KEY";

function verify() {
  $nonce = $_SERVER['HTTP_CANNY_NONCE'];
  $signature = $_SERVER['HTTP_CANNY_SIGNATURE'];

  $calculated = base64_encode(hash_hmac('sha256', $nonce, APIKey, true));
  return $calculated == $signature;
}`,
  python: `import requests
import hmac
import hashlib
import base64

def verify (request: requests.Response):
  nonce = request.headers.get('canny-nonce')
  signature = request.headers.get('canny-signature')

  APIKey = 'YOUR_API_KEY'

  hash = hmac.new(
    APIKey.encode('utf-8'),
    msg=nonce.encode('utf-8'),
    digestmod=hashlib.sha256
  ).digest()

  calculated = base64.b64encode(hash).decode()

  return signature == calculated`,
  node: `import crypto from 'crypto';

function verify(request) {
  const {
    'canny-nonce': nonce,
    'canny-signature': signature
  } = request.headers;

  const APIKey = 'YOUR_API_KEY';

  const calculated = crypto
    .createHmac('sha256', APIKey)
    .update(nonce)
    .digest('base64');

  return signature === calculated;
}`,
  ruby: `require 'openssl'
require 'base64'
require 'net/http'

def verify(response)
  nonce = response['canny-nonce']
  signature = response['canny-signature']

  api_key = 'YOUR_API_KEY'

  calculated = Base64.strict_encode64(OpenSSL::HMAC.digest('sha256', api_key, nonce))

  calculated == signature
end`,
};

export default class APIDocsWebhookSignatureBlock extends Component {
  render() {
    return (
      <APIDocsBlock>
        <div>
          <div className="heading">Webhook signatures</div>
          <div className="text">
            Canny signs all the webhooks it sends. Each request will include the following headers.
          </div>
          <APIDocsTable
            title="Headers"
            rows={[
              {
                description:
                  'The number of milliseconds since the UNIX epoch. Use this field to avoid replayed requests.',
                heading: 'canny-timestamp',
              },
              {
                description: 'A random string, unique per request.',
                heading: 'canny-nonce',
              },
              {
                description:
                  "An HMAC (SHA-256) signature of the nonce, using your team's API key, encoded in Base64",
                heading: 'canny-signature',
              },
            ]}
          />
          <div className="text">
            You can verify a request originated from Canny by using{' '}
            <a href="https://canny.io/redirect?to=%2Fadmin%2Fsettings%2Fapi">your team's API key</a>{' '}
            to verify the signature sent in the <div className="emphasize">canny-signature</div>{' '}
            header, by signing the nonce in <div className="emphasize">canny-nonce</div>. The nonce
            is signed using <a href="https://en.wikipedia.org/wiki/SHA-2">SHA-256</a> and is Base64
            encoded.
          </div>
        </div>
        <div>
          <div className="text">Example verification function</div>
          <div className="multiLanguageContainer">
            <MultiLanguageCodeBlock
              colorScheme="dark"
              blocks={[
                {
                  name: 'Node.js',
                  code: Snippets.node,
                  language: 'js',
                },
                {
                  name: 'C#',
                  language: 'csharp',
                  code: Snippets.csharp,
                },
                {
                  name: 'Go',
                  language: 'go',
                  code: Snippets.go,
                },
                {
                  name: 'Java',
                  language: 'java',
                  code: Snippets.java,
                },
                {
                  name: 'PHP',
                  language: 'php',
                  code: Snippets.php,
                },
                {
                  name: 'Python',
                  language: 'python',
                  code: Snippets.python,
                },
                {
                  name: 'Ruby',
                  language: 'ruby',
                  code: Snippets.ruby,
                },
              ]}
            />
          </div>
        </div>
      </APIDocsBlock>
    );
  }
}
